import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayIcon from '@mui/icons-material/Replay';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import AlbumIcon from '@mui/icons-material/Album';
import { colors, headingFont, uiFont } from '../theme';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import useYouTubePlayer from '../hooks/useYouTubePlayer';
import { formatPlaybackTime } from '../utils/youtubePlayer';
import { getProductDisplayName, getProductImageSrcSet, getProductImageUrl, getProductVideos, getYoutubeEmbedUrl } from '../utils/product';

// Keep the console and YouTube lifecycle immediate; load drag controls only on playback.
const Slider = lazy(() => import('@mui/material/Slider'));
const sliderPlaceholder = <Box aria-hidden="true" sx={{ flex: 1, minWidth: 0, height: 28,
  background: `linear-gradient(${colors.accent}, ${colors.accent}) center / 100% 2px no-repeat`, opacity: 0.38 }} />;

const controlSx = {
  width: 44, height: 44, borderRadius: 1, flexShrink: 0, color: colors.text,
  border: `1px solid ${colors.controlBorder}`, backgroundColor: colors.surface,
  '&:hover': { borderColor: colors.accent, backgroundColor: colors.tint },
};

function AudioPlayerBar() {
  const { currentPlayback, currentProduct, currentVideo, isExpanded, closePlayer, toggleExpanded, playVideo } = useAudioPlayer();
  const consoleRef = useRef(null);
  const playerContainerRef = useRef(null);
  const [consoleHeight, setConsoleHeight] = useState(0);
  const [seekDraft, setSeekDraft] = useState(null);
  const embedUrl = getYoutubeEmbedUrl(currentVideo?.url);
  const displayTitle = currentProduct ? getProductDisplayName(currentProduct) : '';
  const trackTitle = currentVideo?.title || 'Preview';
  const player = useYouTubePlayer({
    containerRef: playerContainerRef, videoUrl: currentVideo?.url,
    title: `${displayTitle} - ${trackTitle}`, enabled: Boolean(currentProduct && embedUrl),
    action: currentPlayback?.action, requestId: currentPlayback?.requestId,
  });

  useEffect(() => { setSeekDraft(null); }, [currentVideo?.url, isExpanded]);

  // Reserve space so the fixed console never covers the last page actions.
  useEffect(() => {
    if (!consoleRef.current) return;
    const measure = () => setConsoleHeight(consoleRef.current?.getBoundingClientRect().height || 0);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(consoleRef.current);
    return () => observer.disconnect();
  }, [embedUrl, isExpanded]);

  if (!currentProduct || !embedUrl) return null;
  const previews = getProductVideos(currentProduct).filter(video => getYoutubeEmbedUrl(video.url));
  const selectedIndex = previews.findIndex(video => video.url === currentVideo.url);
  const previewNumber = selectedIndex >= 0 ? `${String(selectedIndex + 1).padStart(2, '0')} / ${String(previews.length).padStart(2, '0')}` : 'PREVIEW';
  const selectPreview = (video) => {
    if (!video) return;
    if (video.url === currentVideo.url) {
      player.seek(0);
    }
    playVideo(currentProduct, video, { preserveExpanded: true });
  };
  const handlePlayPause = () => {
    if (player.status === 'error') {
      playVideo(currentProduct, currentVideo, { preserveExpanded: true });
      player.retry();
    }
    else if (player.isPlaying) player.pause();
    else player.play();
  };
  const handleClose = () => {
    player.pause();
    closePlayer();
  };
  const loading = (!player.ready || player.playPending) && player.status !== 'error';
  const playLabel = loading ? 'Cargando reproductor' : player.status === 'error' ? 'Reintentar reproductor' : player.isPlaying ? 'Pausar preview' : 'Reproducir preview';
  const position = Math.min(player.duration, seekDraft ?? player.currentTime);
  const statusLabel = {
    idle: 'PREVIEW SELECCIONADO', loading: 'CARGANDO', cued: 'LISTO', playing: 'SONANDO',
    paused: 'PAUSADO', buffering: 'CARGANDO', ended: 'FINALIZADO', blocked: 'PAUSADO', error: 'NO DISPONIBLE',
  }[player.status];

  return (
    <Box sx={{ height: consoleHeight }}>
      <Box component="section" ref={consoleRef} aria-label="Consola DiscoxDiscos" sx={{
        position: 'fixed', inset: 'auto 0 0', zIndex: 1290, backgroundColor: colors.page,
        borderTop: `1px solid ${colors.controlBorder}`, boxShadow: '0 -8px 32px rgba(0,0,0,0.5)',
        maxHeight: '80dvh', overflowY: 'auto', pb: 'env(safe-area-inset-bottom)',
      }}>
        <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 1.5, sm: 3 }, py: { xs: 1, sm: 1.5 },
          display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', md: 'minmax(0, 1fr) minmax(0, 0.65fr) auto' },
          alignItems: 'center', gap: { xs: 1, md: 3 },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            {currentProduct.image ? (
              <Box component="img" src={getProductImageUrl(currentProduct.image, 'thumb')}
                srcSet={getProductImageSrcSet(currentProduct.image, 'thumb')} sizes="(min-width: 600px) 64px, 48px"
                alt={`Tapa de ${displayTitle}`} width={64} height={64} decoding="async"
                sx={{ width: { xs: 48, sm: 64 }, height: { xs: 48, sm: 64 }, objectFit: 'cover', flexShrink: 0, borderRadius: 1 }} />
            ) : <AlbumIcon aria-hidden="true" sx={{ fontSize: 48, color: colors.accent, flexShrink: 0 }} />}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: colors.accent, display: 'block', fontSize: '0.6rem', letterSpacing: '0.08em' }}>
                CONSOLA / DISCOXDISCOS
              </Typography>
              <Typography title={displayTitle} sx={{ fontFamily: headingFont, fontWeight: 400, textTransform: 'uppercase',
                fontSize: { xs: '1rem', sm: '1.25rem' }, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', my: 0.4,
              }}>{displayTitle}</Typography>
              <Typography variant="caption" sx={{ color: colors.muted, display: 'block', fontSize: '0.6rem',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {[currentProduct.style, currentProduct.label].filter(Boolean).join(' / ')}
              </Typography>
              <Typography variant="caption" sx={{ display: { xs: 'block', md: 'none' }, color: colors.secondary,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.65rem',
              }}>{previewNumber} / {trackTitle}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'block' }, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: colors.accent, fontSize: '0.65rem' }}>{statusLabel} / {previewNumber}</Typography>
            <Typography variant="body2" title={trackTitle} sx={{ fontFamily: uiFont, mt: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {trackTitle}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap', gridColumn: { xs: '1 / -1', sm: '2', md: '3' }, justifyContent: { xs: 'flex-end', sm: 'initial' } }}>
            <Tooltip title="Preview anterior">
              <span><IconButton aria-label="Preview anterior" disabled={selectedIndex <= 0} onClick={() => selectPreview(previews[selectedIndex - 1])} sx={controlSx}><SkipPreviousIcon /></IconButton></span>
            </Tooltip>
            <Tooltip title={playLabel}>
              <span><IconButton aria-label={playLabel} disabled={loading} onClick={handlePlayPause}
                sx={{ ...controlSx, backgroundColor: colors.accent, color: colors.page,
                  '&:hover': { backgroundColor: colors.focus }, '&.Mui-disabled': { backgroundColor: colors.elevated, color: colors.muted } }}>
                {loading ? <CircularProgress size={18} color="inherit" /> : player.status === 'error' ? <ReplayIcon /> : player.isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton></span>
            </Tooltip>
            <Tooltip title="Preview siguiente">
              <span><IconButton aria-label="Preview siguiente" disabled={selectedIndex < 0 || selectedIndex >= previews.length - 1} onClick={() => selectPreview(previews[selectedIndex + 1])} sx={controlSx}><SkipNextIcon /></IconButton></span>
            </Tooltip>
            <Tooltip title={isExpanded ? 'Contraer reproductor' : 'Expandir reproductor'}>
              <IconButton onClick={toggleExpanded}
                aria-label={isExpanded ? 'Contraer reproductor' : 'Expandir reproductor'} aria-expanded={isExpanded}
                aria-controls={isExpanded ? 'dxd-console-player' : undefined}
                sx={controlSx}>
                {isExpanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Abrir preview en YouTube">
              <IconButton component="a" href={currentVideo.url} target="_blank" rel="noopener noreferrer"
                aria-label="Abrir preview en YouTube" sx={controlSx}><OpenInNewIcon /></IconButton>
            </Tooltip>
            <Tooltip title="Cerrar reproductor">
              <IconButton aria-label="Cerrar reproductor" onClick={handleClose} sx={controlSx}><CloseIcon /></IconButton>
            </Tooltip>
          </Stack>
        </Box>
        <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 1.5, sm: 3 }, pb: 1,
          display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="caption" sx={{ color: colors.secondary, fontVariantNumeric: 'tabular-nums', minWidth: 36 }}>
            {formatPlaybackTime(position)}
          </Typography>
          <Suspense fallback={sliderPlaceholder}>
          <Slider aria-label="Posición del preview" min={0} max={player.duration || 1} step={1}
            value={position} disabled={!player.ready || !player.duration}
            getAriaValueText={value => `${formatPlaybackTime(value)} de ${formatPlaybackTime(player.duration)}`}
            onChange={(_, value) => setSeekDraft(value)}
            onChangeCommitted={(_, value) => { player.seek(value); setSeekDraft(null); }}
            sx={{ color: colors.accent, flex: 1, minWidth: 0, height: 2, '& .MuiSlider-thumb': { width: 12, height: 12 } }} />
          </Suspense>
          <Typography variant="caption" sx={{ color: colors.secondary, fontVariantNumeric: 'tabular-nums', minWidth: 36 }}>
            {player.duration ? formatPlaybackTime(player.duration) : '--:--'}
          </Typography>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, width: 140, ml: 2 }}>
            <VolumeUpIcon sx={{ color: colors.secondary, fontSize: 18 }} />
            <Suspense fallback={sliderPlaceholder}>
            <Slider aria-label="Volumen del preview" min={0} max={100} value={player.volume} disabled={!player.ready}
              onChange={(_, value) => player.setVolume(value)} getAriaValueText={value => `${value}%`}
              sx={{ color: colors.accent, height: 2, '& .MuiSlider-thumb': { width: 12, height: 12 } }} />
            </Suspense>
          </Box>
        </Box>
        {player.status === 'error' && <Typography role="alert" variant="caption" sx={{ display: 'block', px: 3, pb: 1, color: colors.error }}>
          No se pudo cargar el preview.
        </Typography>}
          <Box id="dxd-console-player" hidden={!isExpanded} sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 1.5, sm: 2 }, pt: 0,
            display: isExpanded ? 'grid' : 'none', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 320px' }, gap: 2,
          }}>
            <Box sx={{ order: 2, width: '100%', maxWidth: 360, mx: 'auto' }}>
              <Box ref={playerContainerRef} sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', minHeight: 200,
                backgroundColor: colors.surface, '& iframe': { position: 'absolute', inset: 0, display: 'block', width: '100%', height: '100%', border: 0 } }} />
            </Box>
            <Box sx={{ minWidth: 0, order: 1 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography component="h3" sx={{ fontFamily: uiFont, fontSize: '0.7rem', textTransform: 'uppercase', color: colors.secondary }}>
                  Previews / {previewNumber}
                </Typography>
              </Stack>
              <Stack spacing={0.75}>
                {previews.map((video, index) => (
                  <Button key={`${video.url}-${index}`} onClick={() => selectPreview(video)} aria-pressed={video.url === currentVideo.url}
                    startIcon={<PlayArrowIcon />} sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none',
                      color: video.url === currentVideo.url ? colors.accent : colors.text,
                      backgroundColor: video.url === currentVideo.url ? colors.tint : colors.surface,
                      border: `1px solid ${video.url === currentVideo.url ? colors.accent : colors.border}`, px: 1.25,
                      fontFamily: uiFont, fontSize: '0.72rem', overflowWrap: 'anywhere',
                    }}>{String(index + 1).padStart(2, '0')} / {video.title}</Button>
                ))}
              </Stack>
            </Box>
          </Box>
      </Box>
      <Box role="status" aria-live="polite" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' }}>
        {statusLabel}: {displayTitle}, {trackTitle}
      </Box>
    </Box>
  );
}

export default AudioPlayerBar;
